
import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { devLog, devError } from "@/utils/devLog";
import { useAuth } from "@/providers/AuthProvider";

interface LocationState {
  from?: string;
  isFreshSignup?: boolean;
}

const RoleSelection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState<'student' | 'creator' | null>(null);
  const roleUpdateInProgressRef = useRef<boolean>(false);
  const { user, session } = useAuth();
  
  const { from, isFreshSignup } = (location.state as LocationState) || {};
  
  // Get session on component mount
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) return; // still loading auth
      
      if (from?.startsWith('/') && from.split('/').length === 3) {
        // enrollment flow: skip selection and go to checkout
        devLog("[RoleSelection] Enrollment flow detected, redirecting to checkout", { from });
        navigate(`/checkout${from}`, { replace: true });
      } else if (!isFreshSignup) {
        // someone arrived here by mistake (e.g. via direct URL) → send to dashboard
        devLog("[RoleSelection] Non-fresh signup detected, redirecting to dashboard");
        navigate('/dashboard/student', { replace: true });
      }
      
      // else: fresh sign-up from home → stay on this page and show the role menu
      devLog("[RoleSelection] Fresh signup detected, showing role selection", {
        from,
        isFreshSignup
      });
    };
    
    checkSession();
  }, [from, isFreshSignup, navigate]);

  useEffect(() => {
    devLog("[RoleSelection] Component mounted with 'from' state:", from);
  }, [from]);

  const handleRoleSelection = async () => {
    if (!selectedRole) {
      toast.error("Please select a role to continue");
      return;
    }

    // Guard against double submission
    if (roleUpdateInProgressRef.current) {
      devLog("[RoleSelection] Role update already in progress, preventing duplicate");
      return;
    }
    
    roleUpdateInProgressRef.current = true;
    setIsLoading(true);
    devLog(`[RoleSelection] Setting role to: ${selectedRole}`);

    try {
      // Get user ID from session or auth context - fixed to handle both sources reliably
      let userId = user?.id;
      
      // If user ID is not available in the context, try to get it from the session
      if (!userId && session?.user) {
        userId = session.user.id;
      }
      
      // If still no user ID, get a fresh session
      if (!userId) {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        userId = currentSession?.user?.id;
      }
      
      // Final check if we have a user ID
      if (!userId) {
        devError("[RoleSelection] No user ID found in session", { userId });
        toast.error("Authentication error. Please log in again.");
        navigate('/login', { replace: true });
        return;
      }
      
      devLog(`[RoleSelection] Setting role for user ${userId} to ${selectedRole}`);

      // Call the set_user_role function using RPC
      devLog("[RoleSelection] Calling set_user_role RPC function");
      const { data, error } = await supabase.rpc('set_user_role', { 
        user_id: userId, 
        new_role: selectedRole 
      });

      if (error) {
        throw error;
      }

      // Better error handling for unexpected responses
      if (data !== true) {
        devError("[RoleSelection] Unexpected response from set_user_role:", data);
        throw new Error("Role update failed - unexpected response");
      }

      devLog(`[RoleSelection] ✅ Successfully updated role to ${selectedRole}`, { data, error });
      
      // Force invalidate queries to ensure fresh data
      devLog("[RoleSelection] Invalidating cached queries");
      await queryClient.invalidateQueries();
      
      // Store critical data in localStorage for persistence
      localStorage.setItem('user_role', selectedRole);
      
      // Determine redirect path based on role and from state
      let redirectPath;
      
      if (selectedRole === 'student') {
        // 1) From a public-course-enroll
        if (from?.startsWith('/') && from.split('/').length === 3) {
          // e.g. "/creatorX/courseY" - treat as course-enroll context
          redirectPath = `/checkout${from}`;
          devLog(`[RoleSelection] Student role with course path, redirecting to: ${redirectPath}`);
        } else {
          // Regular student path
          redirectPath = '/dashboard/student';
          devLog(`[RoleSelection] Student role, redirecting to dashboard: ${redirectPath}`);
        }
      } else {
        // Creator always goes to onboarding/username first
        redirectPath = '/onboarding/username';
        devLog(`[RoleSelection] Creator role, redirecting to: ${redirectPath}`);
      }
      
      toast.success(`Your role has been set to ${selectedRole}`);
      
      // For creator role, use window.location.href for redirect
      if (selectedRole === 'creator') {
        devLog("[RoleSelection] Using location.href for redirect to creator path");
        window.location.href = redirectPath;
      } else {
        devLog("[RoleSelection] Using React Router navigate for student path");
        navigate(redirectPath, { replace: true, state: { from } });
      }
      
    } catch (error: any) {
      devError("[RoleSelection] ❌ Error setting role:", error);
      toast.error(error.message || "Failed to set role. Please try again.");
    } finally {
      setIsLoading(false);
      roleUpdateInProgressRef.current = false;
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1 flex items-center justify-center py-12 px-4 bg-primary-50">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-heading">Choose Your Role</CardTitle>
            <CardDescription>
              Select how you want to use our platform
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <Button
                variant={selectedRole === 'student' ? "default" : "outline"}
                className="h-auto py-6 flex flex-col items-center justify-center gap-2"
                onClick={() => setSelectedRole('student')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                  <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                </svg>
                <span>Student</span>
                <span className="text-xs text-center mt-1">
                  Take courses and learn
                </span>
              </Button>
              
              <Button
                variant={selectedRole === 'creator' ? "default" : "outline"}
                className="h-auto py-6 flex flex-col items-center justify-center gap-2"
                onClick={() => setSelectedRole('creator')}
                data-testid="creator-role-button"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 20h9"></path>
                  <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"></path>
                </svg>
                <span>Creator</span>
                <span className="text-xs text-center mt-1">
                  Create and sell courses
                </span>
              </Button>
            </div>
            
            <Button 
              onClick={handleRoleSelection} 
              className="w-full mt-6" 
              disabled={!selectedRole || isLoading}
              data-testid="continue-button"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Setting Role...
                </>
              ) : (
                "Continue"
              )}
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default RoleSelection;
